<template>
  <div id="page-customerRegister">
    <div class="container">
      <div class="header">
        <div class="step-indicator">{{ currentStep }} / {{ totalSteps }}</div>
      </div>

      <swiper
        :modules="modules"
        :slides-per-view="1"
        :space-between="0"
        :allow-touch-move="false"
        @swiper="onSwiper"
        class="register-swiper"
      >
        <!-- 1. 이름 -->
        <swiper-slide>
          <div class="step-content-single">
            <div class="icon">👤</div>
            <h1>성함을 입력해주세요</h1>
            <input 
              type="text" 
              v-model="newCustomer.name" 
              placeholder="예) 홍길동"
              @keyup.enter="nextStep"
              @keydown.tab.prevent="nextStep"
              class="main-input"
              autofocus
            >
            
            <!-- 별칭 추가 영역 -->
            <div class="optional-field">
              <button 
                v-if="!showNickname" 
                @click="showNickname = true" 
                class="toggle-btn"
              >
                <i class="ph ph-plus"></i> 별칭 추가
              </button>
              <div v-else class="optional-input-wrap">
                <label>별칭</label>
                <input 
                  type="text" 
                  v-model="newCustomer.nickname" 
                  placeholder="예) 길동이, 회사동료"
                  @keyup.enter="nextStep"
                  class="sub-input"
                >
              </div>
            </div>
            
            <p class="desc">설계사님께 전달될 정보입니다</p>
          </div>
        </swiper-slide>

        <!-- 2. 전화번호 -->
        <swiper-slide>
          <div class="step-content-single">
            <div class="icon">📞</div>
            <h1>연락처</h1>
            <input 
              type="tel" 
              v-model="newCustomer.phone" 
              placeholder="-없이 번호만 입력해주세요"
              @keyup.enter="nextStep"
              @keydown.tab.prevent="nextStep"
              @input="formatPhone"
              class="main-input"
            >
            <p class="desc">상담 및 선물 전달을 위해 필요합니다</p>
          </div>
        </swiper-slide>

        <!-- 3. 주민번호 -->
        <swiper-slide>
          <div class="step-content-single">
            <div class="icon">🪪</div>
            <h1>생년월일 - 성별</h1>
            
            <div class="double">
              <input 
                type="text" 
                v-model="firstCivNum" 
                placeholder="990101"
                maxLength="6"
                @keyup.enter="focusLastCivNum"
                @keydown.tab.prevent="focusLastCivNum"
                @input="formatCivNum('first')"
                class="main-input"
                style="flex:1"
              >
              <h1>-</h1>
              <input 
                ref="civNumLastInput"
                type="password"
                autocomplete="new-password"
                v-model="lastCivNum" 
                placeholder="●"
                maxLength="1"
                @keyup.enter="nextStep"
                @keydown.tab.prevent="nextStep"
                @input="formatCivNum('last')"
                style="flex:0.2"
                class="main-input"
              >
            </div>

            <!-- 실제 생일 추가 영역 -->
            <div class="optional-field">
              <button 
                v-if="!showRealBirth" 
                @click="showRealBirth = true" 
                class="toggle-btn"
              >
                <i class="ph ph-calendar-plus"></i> 실제 생일이 다른가요?
              </button>
              <div v-else class="optional-input-wrap">
                <label>실제 생일</label>
                <input 
                  type="text" 
                  v-model="newCustomer.birth" 
                  placeholder="예) 1999-01-15"
                  maxLength="10"
                  @input="formatBirth"
                  @keyup.enter="nextStep"
                  class="sub-input"
                >
                <span class="helper-text">음력생일, 실제생일 등 주민등록과 다른 경우</span>
              </div>
            </div>

            <p class="desc" v-if="!showRealBirth">보험 설계를 위해 필요한 정보입니다</p>
          </div>
        </swiper-slide>

        <!-- 4. 주소 -->
        <swiper-slide>
          <div class="step-content-single">
            <div class="icon">🏠</div>
            <h1>주소</h1>
            
            <div class="double">
              <input 
                type="text" 
                v-model="newCustomer.address" 
                placeholder="예) 서울특별시 강남구"
                @keyup.enter="focusExAddress"
                @keydown.tab.prevent="focusExAddress"
                class="main-input"
              >
              <button @click="openDaumPostcode" class="find-btn">찾기</button>
            </div>

            <input 
              ref="exAddressInput"
              type="text" 
              v-model="newCustomer.ex_address" 
              placeholder="상세 주소 (선택)"
              @keyup.enter="nextStep"
              @keydown.tab.prevent="nextStep"
              class="main-input"
              style="margin-top: 12px;"
            >
            
            <p class="desc">선물 발송을 위해 필요합니다</p>
          </div>
        </swiper-slide>

        <!-- 5. 수신동의 -->
        <swiper-slide>
          <div class="step-content-single agreement">
            <div class="icon">📋</div>
            <h1>수신동의</h1>
            <p class="desc" style="margin-top: 0;">더 나은 서비스를 위해 동의해주세요</p>

            <div class="agreement-list">
              <!-- 전체 동의 -->
              <div class="agreement-item all" @click="toggleAllAgreements">
                <div class="checkbox" :class="{ checked: isAllAgreed }">
                  <i class="ph-bold ph-check" v-if="isAllAgreed"></i>
                </div>
                <div class="agreement-text">
                  <span class="label">전체 동의</span>
                </div>
              </div>

              <div class="divider"></div>

              <!-- 개인정보 수집 동의 (필수) -->
              <div class="agreement-item" @click="agreements.privacy = !agreements.privacy">
                <div class="checkbox" :class="{ checked: agreements.privacy }">
                  <i class="ph-bold ph-check" v-if="agreements.privacy"></i>
                </div>
                <div class="agreement-text">
                  <span class="label">개인정보 수집 및 이용 동의</span>
                  <span class="badge required">필수</span>
                </div>
                <button class="view-btn" @click.stop="showPrivacyModal = true">보기</button>
              </div>

              <!-- SMS 수신 동의 (선택) -->
              <div class="agreement-item" @click="agreements.sms = !agreements.sms">
                <div class="checkbox" :class="{ checked: agreements.sms }">
                  <i class="ph-bold ph-check" v-if="agreements.sms"></i>
                </div>
                <div class="agreement-text">
                  <span class="label">SMS 수신 동의</span>
                  <span class="badge optional">선택</span>
                </div>
              </div>

              <!-- 이메일 수신 동의 (선택) -->
              <div class="agreement-item" @click="agreements.email = !agreements.email">
                <div class="checkbox" :class="{ checked: agreements.email }">
                  <i class="ph-bold ph-check" v-if="agreements.email"></i>
                </div>
                <div class="agreement-text">
                  <span class="label">이메일 수신 동의</span>
                  <span class="badge optional">선택</span>
                </div>
              </div>
            </div>

            <p class="notice">
              <i class="ph ph-info"></i>
              선택 항목에 동의하시면 유용한 보험 정보와 이벤트 소식을 받아보실 수 있습니다.
            </p>
          </div>
        </swiper-slide>

        <!-- 6. 완료 -->
        <swiper-slide>
          <div class="step-content-single completion">
            <div class="icon success">
              <i class="ph-fill ph-check"></i>
            </div>
            <h1>등록 완료!</h1>
            <p class="desc-large">
              <strong>{{ plannerName }}</strong> 설계사에게<br>
              소중한 정보 남겨주셔서 감사합니다! 🎉
            </p>
            <p class="desc">소정의 선물을 {{ plannerName }} 설계사가 건네드릴 겁니다!</p>

            <div class="summary">
              <div class="summary-item" v-if="newCustomer.name || newCustomer.nickname">
                <i class="ph-fill ph-user"></i>
                <span>{{ newCustomer.name || newCustomer.nickname }}</span>
              </div>
              <div class="summary-item" v-if="newCustomer.phone">
                <i class="ph-fill ph-phone"></i>
                <span>{{ newCustomer.phone }}</span>
              </div>
              <div class="summary-item" v-if="firstCivNum && lastCivNum">
                <i class="ph-fill ph-identification-card"></i>
                <span>{{ firstCivNum }}-{{ lastCivNum }}******</span>
              </div>
              <div class="summary-item" v-if="newCustomer.birth">
                <i class="ph-fill ph-cake"></i>
                <span>{{ newCustomer.birth }} (실제생일)</span>
              </div>
              <div class="summary-item" v-if="newCustomer.address">
                <i class="ph-fill ph-house"></i>
                <span>{{ newCustomer.address }} {{ newCustomer.ex_address }}</span>
              </div>
              <div class="summary-item">
                <i class="ph-fill ph-bell"></i>
                <span>
                  {{ agreements.sms ? 'SMS ✓' : '' }}
                  {{ agreements.email ? '이메일 ✓' : '' }}
                  {{ !agreements.sms && !agreements.email ? '수신동의 없음' : '' }}
                </span>
              </div>
            </div>

            <button @click="finish" class="action-btn primary">
              <i class="ph-fill ph-check"></i>
              마침
            </button>
          </div>
        </swiper-slide>
      </swiper>

      <div class="nav-buttons" v-if="currentStep < totalSteps">
        <button 
          v-if="currentStep > 1" 
          @click="prevStep" 
          class="nav-btn prev"
        >
          <i class="ph ph-arrow-left"></i>
        </button>
        
        <button 
          @click="handleNext" 
          class="nav-btn next"
          :class="{ primary: currentStep === 1, disabled: currentStep === 5 && !agreements.privacy }"
          :disabled="currentStep === 5 && !agreements.privacy"
        >
          <i class="ph ph-arrow-right"></i>
        </button>
      </div>
    </div>

    <!-- 개인정보 처리방침 모달 -->
    <div class="modal-overlay" v-if="showPrivacyModal" @click="showPrivacyModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>개인정보 수집 및 이용 동의</h2>
          <button @click="showPrivacyModal = false" class="close-btn">
            <i class="ph ph-x"></i>
          </button>
        </div>
        <div class="modal-body">
          <p><strong>1. 수집하는 개인정보 항목</strong></p>
          <p>성명, 연락처, 생년월일, 주소</p>
          
          <p><strong>2. 개인정보의 수집 및 이용 목적</strong></p>
          <p>보험 상담 및 설계, 고객 관리, 선물 발송</p>
          
          <p><strong>3. 개인정보의 보유 및 이용기간</strong></p>
          <p>수집일로부터 3년 또는 동의 철회 시까지</p>
          
          <p><strong>4. 동의 거부권 및 불이익</strong></p>
          <p>동의를 거부할 권리가 있으며, 거부 시 서비스 이용이 제한될 수 있습니다.</p>
        </div>
        <button @click="showPrivacyModal = false; agreements.privacy = true" class="modal-agree-btn">
          동의하고 닫기
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { inject, ref, computed, onMounted } from "vue";
import { useRoute } from 'vue-router';
import { Swiper, SwiperSlide } from 'swiper/vue';

export default {
  components: {
    Swiper,
    SwiperSlide,
  },
  setup() {
    
    const route = useRoute();
    const sql_query = inject('sql_query');
    const member_info = inject('member_info');
    const checkCustomerMilestone = inject('checkCustomerMilestone');
    
    const swiperInstance = ref(null);
    const currentStep = ref(1);
    const totalSteps = ref(6); // 5 → 6으로 변경
    const modules = [];

    const mb_id = ref('');
    const plannerName = ref('설계사님');

    const newCustomer = ref({
      name: '',
      nickname: '',
      phone: '',
      address: '',
      ex_address: '',
      location: '',
      birth: '',
    });

    const firstCivNum = ref('');
    const lastCivNum = ref('');
    const showNickname = ref(false);
    const showRealBirth = ref(false);
    const showPrivacyModal = ref(false);

    // 수신동의
    const agreements = ref({
      privacy: false,
      sms: false,
      email: false,
    });

    const isAllAgreed = computed(() => {
      return agreements.value.privacy && agreements.value.sms && agreements.value.email;
    });

    function toggleAllAgreements() {
      const newValue = !isAllAgreed.value;
      agreements.value.privacy = newValue;
      agreements.value.sms = newValue;
      agreements.value.email = newValue;
    }

    // refs for focus control
    const civNumLastInput = ref(null);
    const exAddressInput = ref(null);

    onMounted(async () => {
      mb_id.value = route.params.mb_id;
      
      if (mb_id.value) {
        const sql = `SELECT name FROM member WHERE mb_id = '${mb_id.value}'`;
        const result = await sql_query(sql);
        if (result && result.length > 0) {
          plannerName.value = result[0].name;
        }
      }
    });

    function formatPhone() {
      let value = newCustomer.value.phone.replace(/[^0-9]/g, '');
      if (value.length > 11) value = value.slice(0, 11);
      newCustomer.value.phone = value;
    }

    function formatCivNum(type) {
      if (type === 'first') {
        firstCivNum.value = firstCivNum.value.replace(/[^0-9]/g, '');
        if (firstCivNum.value.length > 6) {
          firstCivNum.value = firstCivNum.value.slice(0, 6);
        }
      } else {
        lastCivNum.value = lastCivNum.value.replace(/[^0-9]/g, '');
        if (lastCivNum.value.length > 1) {
          lastCivNum.value = lastCivNum.value.slice(0, 1);
        }
      }
    }

    function formatBirth() {
      let value = newCustomer.value.birth.replace(/[^0-9]/g, '');
      if (value.length > 8) value = value.slice(0, 8);
      
      if (value.length >= 5) {
        value = value.slice(0, 4) + '-' + value.slice(4, 6) + (value.length > 6 ? '-' + value.slice(6) : '');
      } else if (value.length >= 4) {
        value = value.slice(0, 4) + '-' + value.slice(4);
      }
      
      newCustomer.value.birth = value;
    }

    function focusLastCivNum() {
      setTimeout(() => {
        civNumLastInput.value?.focus();
      }, 50);
    }

    function focusExAddress() {
      setTimeout(() => {
        exAddressInput.value?.focus();
      }, 50);
    }

    function onSwiper(swiper) {
      swiperInstance.value = swiper;
    }

    function handleNext() {
      // 수신동의 단계에서 필수 동의 체크
      if (currentStep.value === 5 && !agreements.value.privacy) {
        alert('개인정보 수집 및 이용에 동의해주세요.');
        return;
      }
      
      if (currentStep.value === 5) {
        // 수신동의 후 저장
        saveCustomer();
      }
      nextStep();
    }

    let doingSwipeNext = false;
    async function nextStep() {
      if (currentStep.value < totalSteps.value && !doingSwipeNext) {
        currentStep.value++;
        await swiperInstance.value?.slideNext();
        doingSwipeNext = true
        await setTimeout(() => {
          if (currentStep.value === 2) {
            const phoneInput = document.querySelector('input[type="tel"]');
            phoneInput?.focus();
          } else if (currentStep.value === 3) {
            const civNumInput = document.querySelector('input[placeholder="990101"]');
            civNumInput?.focus();
          } else if (currentStep.value === 4) {
            const addressInput = document.querySelector('input[placeholder="예) 서울특별시 강남구"]');
            addressInput?.focus();
          }
        }, 500);
        doingSwipeNext = false;
      }
    }

    function prevStep() {
      if (currentStep.value > 1) {
        currentStep.value--;
        swiperInstance.value?.slidePrev();
      }
    }

    function escapeStr(value) {
      return value !== null && value !== undefined ? String(value).replace(/'/g, "\\'") : null;
    }

    function sanitizeName(name) {
      if (!name) return '무명';

      const cleaned = name
        .replace(/\s+/g, ' ')
        .replace(/[^\p{L}\p{N} ]/gu, '')
        .trim();

      if (cleaned.length < 2) return '무명';
      if (cleaned.length > 10) return cleaned.slice(0, 10);

      return cleaned;
    }

    async function saveCustomer() {
      newCustomer.value.name = sanitizeName(newCustomer.value.name);

      const c = newCustomer.value;
      const a = agreements.value;
      
      const rrnValue = (firstCivNum.value && lastCivNum.value) 
        ? `${firstCivNum.value}-${lastCivNum.value}` 
        : '';

      let userIP = '';
      try {
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipResponse.json();
        userIP = ipData.ip;
      } catch (e) {
        console.log('IP 조회 실패');
      }

      const now = new Date().toISOString().slice(0, 19).replace('T', ' ');

      const sql = `INSERT INTO customers SET
        mb_id = '${mb_id.value}',
        name = '${escapeStr(c.name)}',
        nickname = ${c.nickname ? `'${escapeStr(c.nickname)}'` : 'NULL'},
        title = '고객님',
        rrn = ${rrnValue ? `'${escapeStr(rrnValue)}'` : 'NULL'},
        birth = ${c.birth ? `'${escapeStr(c.birth)}'` : 'NULL'},
        address = ${c.address ? `'${escapeStr(c.address)}'` : 'NULL'},
        ex_address = ${c.ex_address ? `'${escapeStr(c.ex_address)}'` : 'NULL'},
        location = ${c.location ? `'${escapeStr(c.location)}'` : 'NULL'},
        phone = ${c.phone ? `'${escapeStr(c.phone)}'` : 'NULL'},
        ip = ${userIP ? `'${escapeStr(userIP)}'` : 'NULL'},
        agree_privacy = ${a.privacy ? 1 : 0},
        agree_privacy_at = ${a.privacy ? `'${now}'` : 'NULL'},
        agree_sms = ${a.sms ? 1 : 0},
        agree_sms_at = ${a.sms ? `'${now}'` : 'NULL'},
        agree_email = ${a.email ? 1 : 0},
        agree_email_at = ${a.email ? `'${now}'` : 'NULL'}`;

      await sql_query(sql);
      window.dispatchEvent(new CustomEvent('customer-updated'));
      // 등록 성공 후 자동으로 마일스톤 체크
      checkCustomerMilestone(member_info.mb_id);
    }

    function openDaumPostcode() {
      new window.daum.Postcode({
        oncomplete: function(data) {
          newCustomer.value.address = data.address + ' ' + data.buildingName;
          newCustomer.value.location = data.sido + ' ' + data.sigungu;
        }
      }).open();
    }

    function finish() {
      alert('감사합니다! 곧 연락드리겠습니다.');
      window.close();
    }

    return {
      swiperInstance,
      currentStep,
      totalSteps,
      modules,
      newCustomer,
      firstCivNum,
      lastCivNum,
      plannerName,
      civNumLastInput,
      exAddressInput,
      showNickname,
      showRealBirth,
      showPrivacyModal,
      agreements,
      isAllAgreed,
      toggleAllAgreements,
      onSwiper,
      handleNext,
      nextStep,
      prevStep,
      formatPhone,
      formatCivNum,
      formatBirth,
      focusLastCivNum,
      focusExAddress,
      openDaumPostcode,
      finish,
    };
  }
}
</script>

<style scoped>
#page-customerRegister {
  width: 100%;
  min-height: 100vh;
  background: var(--color-surface);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  box-sizing: border-box;
}

.container {
  position: relative;
  width: 100%;
  max-width: 500px;
  height: 85vh;
  max-height: 800px;
  background: var(--color-background);
  border-radius: var(--radius-lg);
  padding: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-md);
  overflow: hidden;
}

.header {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 28px;
  border-bottom: 1px solid var(--color-background-light);
}

.step-indicator {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-brand);
  background: var(--color-brand-light);
  padding: 8px 16px;
  border-radius: var(--radius-lg);
}

.register-swiper {
  flex: 1;
  width: 100%;
  overflow: hidden;
}

.step-content-single {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px 32px;
  box-sizing: border-box;
  gap: 24px;
  text-align: center;
}

.icon {
  font-size: var(--font-size-5xl);
  animation: bounceIn 0.6s ease-out;
  filter: drop-shadow(var(--shadow-sm));
}

@keyframes bounceIn {
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.icon.success {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, var(--color-special-point), #45b049);
  color: var(--color-text-white);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: var(--font-size-4xl);
  box-shadow: var(--shadow-md);
}

h1 {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-extrabold);
  color: var(--color-text-primary);
  margin: 0;
  letter-spacing: -0.8px;
  line-height: 1.3;
}

.desc {
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
  margin: -12px 0 0 0;
  line-height: 1.5;
}

.desc-large {
  font-size: var(--font-size-lg);
  color: var(--color-text-primary);
  margin: -12px 0 0 0;
  line-height: 1.6;
}

.desc-large strong {
  color: var(--color-brand);
  font-weight: var(--font-weight-extrabold);
}

.main-input {
  width: 100%;
  padding: 18px 20px;
  box-sizing: border-box;
  border: 2px solid var(--color-background-light);
  border-radius: var(--radius-md);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  background: var(--color-background);
  color: var(--color-text-primary);
  transition: all 0.3s;
  text-align: center;
}

.double {
  width: 100%;
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
}

.main-input:focus {
  outline: none;
  border-color: var(--color-brand);
  box-shadow: 0 0 0 4px var(--color-brand-light);
  transform: scale(1.02);
}

.main-input::placeholder {
  color: var(--color-text-secondary);
  opacity: 0.6;
  font-weight: var(--font-weight-medium);
}

.find-btn {
  padding: 18px 24px;
  background: var(--color-brand);
  color: var(--color-text-black);
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.find-btn:hover {
  opacity: 0.9;
  transform: translateY(-2px);
}

.step-content-single.completion {
  gap: 20px;
}

.summary {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  background: var(--color-background-light);
  border-radius: var(--radius-lg);
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--color-surface-reverse);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-item i {
  font-size: var(--font-size-xl);
  color: var(--color-brand);
}

.action-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  border: none;
  border-radius: var(--radius-lg);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 12px;
}

.action-btn.primary {
  background: var(--color-brand);
  color: var(--color-text-black);
  box-shadow: var(--shadow-md);
}

.action-btn.primary:hover {
  opacity: 0.9;
  transform: translateY(-2px);
}

.nav-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 20px 28px;
  border-top: 1px solid var(--color-background-light);
}

.nav-btn {
  width: 56px;
  height: 56px;
  border: none;
  border-radius: 50%;
  background: var(--color-background-light);
  color: var(--color-text-primary);
  font-size: var(--font-size-xl);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: var(--shadow-sm);
}

.nav-btn:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-md);
}

.nav-btn.next.primary {
  background: var(--color-brand);
  color: var(--color-text-black);
  box-shadow: var(--shadow-md);
}

.nav-btn.disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.nav-btn.disabled:hover {
  transform: none;
}

/* 옵션 필드 스타일 */
.optional-field {
  width: 100%;
  margin-top: 8px;
}

.toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: transparent;
  border: 1px dashed var(--color-text-secondary);
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.2s;
}

.toggle-btn:hover {
  border-color: var(--color-brand);
  color: var(--color-brand);
  background: var(--color-brand-light);
}

.optional-input-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 6px;
  box-sizing: border-box;
  background: var(--color-background-light);
  border-radius: var(--radius-md);
  animation: fadeSlideIn 0.3s ease-out;
}

@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.optional-input-wrap label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-brand);
  text-align: left;
}

.sub-input {
  width: 100%;
  padding: 14px 16px;
  box-sizing: border-box;
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  background: var(--color-background);
  color: var(--color-text-primary);
  transition: all 0.2s;
  text-align: center;
}

.sub-input:focus {
  outline: none;
  border-color: var(--color-brand);
  box-shadow: 0 0 0 3px var(--color-brand-light);
}

.helper-text {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-align: center;
}

/* 수신동의 스타일 */
.step-content-single.agreement {
  gap: 16px;
  justify-content: flex-start;
  padding-top: 60px;
}

.agreement-list {
  width: 100%;
  background: var(--color-background-light);
  border-radius: var(--radius-lg);
  padding: 8px;
}

.agreement-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  cursor: pointer;
  border-radius: var(--radius-md);
  transition: all 0.2s;
}

.agreement-item:hover {
  background: var(--color-surface);
}

.agreement-item.all {
  background: var(--color-background);
  color:#000;
}

.agreement-item.all:hover {
  background: var(--color-brand);
  opacity: 0.4;
}

.checkbox {
  width: 24px;
  height: 24px;
  border: 2px solid var(--color-text-secondary);
  border-radius: var(--radius-sm);
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.2s;
  flex-shrink: 0;
}

.checkbox.checked {
  background: var(--color-brand);
  border-color: var(--color-brand);
  color: var(--color-text-black);
}

.agreement-text {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
}

.agreement-text .label {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.badge {
  font-size: var(--font-size-xs);
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  font-weight: var(--font-weight-bold);
}

.badge.required {
  background: var(--color-special-point);
  color: var(--color-text-white);
}

.badge.optional {
  background: var(--color-background);
  color: var(--color-text-secondary);
}

.view-btn {
  padding: 6px 12px;
  background: transparent;
  border: 1px solid var(--color-text-secondary);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.view-btn:hover {
  border-color: var(--color-brand);
  color: var(--color-brand);
}

.divider {
  height: 1px;
  background: var(--color-surface-reverse);
  margin: 4px 16px;
}

.notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  text-align: left;
  padding: 12px 16px;
  background: var(--color-surface);
  border-radius: var(--radius-md);
  margin: 0;
}

.notice i {
  font-size: var(--font-size-lg);
  flex-shrink: 0;
}

/* 모달 스타일 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  width: 90%;
  max-width: 400px;
  max-height: 80vh;
  background: var(--color-background);
  border-radius: var(--radius-lg);
  overflow: hidden;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-background-light);
}

.modal-header h2 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  margin: 0;
  color: var(--color-text-primary);
}

.close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  color: var(--color-text-secondary);
  transition: all 0.2s;
}

.close-btn:hover {
  background: var(--color-background-light);
  color: var(--color-text-primary);
}

.modal-body {
  padding: 24px;
  max-height: 300px;
  overflow-y: auto;
}

.modal-body p {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin: 0 0 12px 0;
}

.modal-body p strong {
  color: var(--color-text-primary);
  font-weight: var(--font-weight-semibold);
}

.modal-agree-btn {
  width: 100%;
  padding: 16px;
  background: var(--color-brand);
  color: var(--color-text-black);
  border: none;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.2s;
}

.modal-agree-btn:hover {
  opacity: 0.9;
}

@media (max-width: 640px) {
  #page-customerRegister {
    padding: 0;
  }

  .container {
    width: 100%;
    height: 100vh;
    border-radius: 0;
    max-height: none;
  }

  .step-content-single {
    padding: 32px 24px;
  }

  .step-content-single.agreement {
    padding-top: 40px;
  }

  h1 {
    font-size: var(--font-size-xl);
  }

  .icon {
    font-size: var(--font-size-4xl);
  }
}
</style>